import React, { useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, Inbox, ChevronDown } from "lucide-react";
import { CARD } from "@/styles/shared";
import { timeAgo } from "@/utils/timeAgo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DASH_CARD = {
  ...CARD,
  background: "rgba(255,255,255,0.82)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(255,255,255,0.7)",
};

function daysOverdue(dateStr) {
  const due = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const diff = Math.floor((today - due) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return "due today";
  return diff === 1 ? "1 day overdue" : `${diff} days overdue`;
}

export default function NeedsAttention({ data, token }) {
  const [overdueExpanded, setOverdueExpanded] = useState(false);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [overdueLoading, setOverdueLoading] = useState(false);

  if (!data) return null;
  const { stale_projects, inbox_overflow, overdue_count } = data;
  const allClear = stale_projects.length === 0 && !inbox_overflow && overdue_count === 0;

  const handleOverdueClick = async () => {
    if (overdueExpanded) { setOverdueExpanded(false); return; }
    setOverdueLoading(true);
    try {
      const { data: tasks } = await axios.get(`${BACKEND_URL}/api/tasks/overdue`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOverdueTasks(tasks);
      setOverdueExpanded(true);
    } catch { /* silent */ }
    finally { setOverdueLoading(false); }
  };

  return (
    <div style={{ ...DASH_CARD, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <div style={{
        padding: "0.875rem 1.25rem",
        borderBottom: "1px solid rgba(26,77,46,0.1)",
        display: "flex", alignItems: "center", gap: "0.5rem",
      }}>
        <AlertTriangle size={16} color={allClear ? "#3d6b1f" : "#d97706"} />
        <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 700 }}>
          Needs Attention
        </span>
      </div>

      <div style={{ padding: "0.75rem 1rem", display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
        {allClear ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem", textAlign: "center" }}>
            <div>
              <CheckCircle2 size={28} color="#3d6b1f" style={{ margin: "0 auto 0.5rem" }} />
              <p style={{
                fontFamily: "'Manrope', sans-serif", color: "#3d6b1f",
                fontSize: "0.85rem", fontWeight: 600, margin: 0,
              }}>
                All clear — great work!
              </p>
            </div>
          </div>
        ) : (
          <>
            {overdue_count > 0 && (
              <div>
                {/* Clickable overdue row */}
                <button
                  data-testid="overdue-tasks-btn"
                  onClick={handleOverdueClick}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: "0.5rem",
                    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)",
                    borderRadius: overdueExpanded ? "0.5rem 0.5rem 0 0" : "0.5rem",
                    padding: "0.5rem 0.75rem", cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(220,38,38,0.1)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(220,38,38,0.06)"; }}
                >
                  <AlertTriangle size={14} color="#dc2626" />
                  <span style={{
                    fontFamily: "'Manrope', sans-serif", fontSize: "0.8rem",
                    color: "#dc2626", fontWeight: 500, flex: 1, textAlign: "left",
                  }}>
                    {overdueLoading ? "Loading..." : `${overdue_count} overdue task${overdue_count !== 1 ? "s" : ""}`}
                  </span>
                  <ChevronDown
                    size={13}
                    color="#dc2626"
                    style={{ transition: "transform 0.2s", transform: overdueExpanded ? "rotate(180deg)" : "none", opacity: 0.7 }}
                  />
                </button>

                {/* Expanded overdue list */}
                {overdueExpanded && (
                  <div style={{
                    border: "1px solid rgba(220,38,38,0.2)", borderTop: "none",
                    borderRadius: "0 0 0.5rem 0.5rem",
                    background: "rgba(255,255,255,0.7)", overflow: "hidden",
                    maxHeight: "200px", overflowY: "auto",
                  }}>
                    {overdueTasks.length === 0 ? (
                      <p style={{
                        padding: "0.75rem", margin: 0,
                        fontFamily: "'Manrope', sans-serif", fontSize: "0.75rem", color: "#9ca3af",
                      }}>
                        No overdue tasks found.
                      </p>
                    ) : overdueTasks.map((t) => (
                      <div
                        key={t.id}
                        data-testid={`overdue-task-${t.id}`}
                        style={{
                          padding: "0.5rem 0.75rem",
                          borderBottom: "1px solid rgba(220,38,38,0.08)",
                          display: "flex", flexDirection: "column", gap: "0.15rem",
                        }}
                      >
                        <span style={{
                          fontFamily: "'Manrope', sans-serif", fontSize: "0.8rem",
                          color: "#1A4D2E", fontWeight: 500, lineHeight: 1.3,
                        }}>
                          {t.title}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <span style={{
                            fontFamily: "'Manrope', sans-serif", fontSize: "0.7rem", color: "#9ca3af",
                          }}>
                            {t.project_emoji} {t.project_title}
                          </span>
                          <span style={{
                            fontFamily: "'Manrope', sans-serif", fontSize: "0.7rem",
                            color: "#dc2626", fontWeight: 500,
                          }}>
                            · {daysOverdue(t.due_date)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {inbox_overflow && (
              <AlertRow
                icon={<Inbox size={14} color="#d97706" />}
                bg="rgba(217,119,6,0.06)"
                border="rgba(217,119,6,0.2)"
                color="#92400e"
                label="Inbox getting full (10+ items)"
              />
            )}
            {stale_projects.map((p) => (
              <AlertRow
                key={p.id}
                icon={<span style={{ fontSize: "0.9rem", lineHeight: 1 }}>{p.emoji}</span>}
                bg="rgba(217,119,6,0.06)"
                border="rgba(217,119,6,0.2)"
                color="#92400e"
                label={`${p.title} — idle ${timeAgo(p.last_touched)}`}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function AlertRow({ icon, bg, border, color, label }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "0.5rem",
      background: bg, border: `1px solid ${border}`,
      borderRadius: "0.5rem", padding: "0.5rem 0.75rem",
    }}>
      {icon}
      <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.8rem", color, fontWeight: 500 }}>
        {label}
      </span>
    </div>
  );
}
